import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from './config';
import logger from './utils/logger';
import {
  FiUploadCloud, FiPlus, FiCheck, FiArrowRight,
  FiSave, FiMapPin, FiClock, FiCalendar, FiAlertTriangle, FiLayout, FiX, FiCamera, FiMail, FiShield
} from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";
import SigningLoader from './SigningLoader';
import './App.css';

// Ensure PDF worker is set up
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function InitiateRequest({ onExit, preSelectedOrgId, initialUseTemplate = false, initialTemplateId = null }) {

  const [step, setStep] = useState(1);

  // Setup Wizard states
  // Start wizard active unless a template was pre-selected (e.g. from Dashboard quick-start)
  const [wizardActive, setWizardActive] = useState(!initialUseTemplate && !initialTemplateId);
  const [wizardStep, setWizardStep] = useState(1); // 1=source, 2=setup, 3=signing-method
  // Wizard file upload state
  const [wizardFile, setWizardFile] = useState(null);         // File object (for display)
  const [wizardUploading, setWizardUploading] = useState(false);
  const [wizardUploadedPath, setWizardUploadedPath] = useState(''); // server path after upload
  const [wizardUploadedUrl, setWizardUploadedUrl] = useState('');   // signed URL for preview
  const [wizardError, setWizardError] = useState('');

  // --- Template States ---
  const [useTemplate, setUseTemplate] = useState(initialUseTemplate);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateFieldValues, setTemplateFieldValues] = useState({});
  const [activeRoleDropdown, setActiveRoleDropdown] = useState(null);

  // --- Preview & Highlighting States ---
  const [previewNumPages, setPreviewNumPages] = useState(null);
  const [previewPageNumber, setPreviewPageNumber] = useState(1);
  const [highlightedItem, setHighlightedItem] = useState(null); // { type: 'text'|'role', label: 'Name' }

  // User Role Logic
  const userRoles = JSON.parse(localStorage.getItem('roles') || "[]");
  const isSuperAdmin = userRoles.includes("SUPER_ADMIN");
  const userOrgId = localStorage.getItem('orgId');
  const targetOrgId = isSuperAdmin ? preSelectedOrgId : userOrgId;

  // CONFIG STATES
  const [captureLocation, setCaptureLocation] = useState(false);
  const [enableReminder, setEnableReminder] = useState(false);
  const [reminderDuration, setReminderDuration] = useState("24"); // hours
  const [enableExpiry, setEnableExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState("");
  const [captureFace, setCaptureFace] = useState(false); // NEW: Face Capture State
  const [emailNotificationEnabled, setEmailNotificationEnabled] = useState(true); // NEW: Email Notification State
  const [otpEnabled, setOtpEnabled] = useState(false); // NEW: OTP Verification State

  const [recipients, setRecipients] = useState([{ name: '', email: '', phone: '', signature_type: 'e-signature', role_labels: [], selectedContactId: null }]);
  const [openLinkRequest, setOpenLinkRequest] = useState(false);
  const [selectedRecipientEmail, setSelectedRecipientEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [contactsList, setContactsList] = useState([]);

  // File States
  const [pdfFile, setPdfFile] = useState(null); // Will hold the File Object for uploads
  const [pdfUrl, setPdfUrl] = useState(''); // Will hold the URL (local or remote)
  const [isUploading] = useState(false);

  // Manual Placement States (Step 2 Logic)
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [sigPositions, setSigPositions] = useState({});
  const [showSignatureBox, setShowSignatureBox] = useState(false);
  const [applyAllPages, setApplyAllPages] = useState(false);
  const [dragAllAtOnce, setDragAllAtOnce] = useState(false);
  const [selectedPages, setSelectedPages] = useState([]);
  const [selectingPages, setSelectingPages] = useState(false);
  const [tempSelectedPages, setTempSelectedPages] = useState([]);

  // UI States
  const [isSaving, setIsSaving] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationModalMessage, setValidationModalMessage] = useState('');
  const renderedPageWidth = 500;
  const navigate = useNavigate();

  // --- TEMPLATE EFFECTS ---
  useEffect(() => {
    if (useTemplate) {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      axios.get(`${baseUrl}/templates/list`)
        .then(res => setTemplates(res.data))
        .catch(err => console.error(err));
    }
  }, [useTemplate]);

  // Load contacts for "From contacts" picker (Step 1)
  useEffect(() => {
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    const params = {};
    if (isSuperAdmin && targetOrgId) params.orgId = targetOrgId;
    axios.get(`${baseUrl}/contacts`, { params })
      .then(res => setContactsList(res.data || []))
      .catch(() => setContactsList([]));
  }, [isSuperAdmin, targetOrgId]);

  // When templates are loaded and an initial template id is provided,
  // auto-select that template and ensure Use Template is active.
  useEffect(() => {
    if (!initialTemplateId || !templates.length) return;
    const tmpl = templates.find(t => t._id === initialTemplateId);
    if (!tmpl) return;
    setUseTemplate(true);
    setSelectedTemplate(tmpl);
    setPdfFile(tmpl.file_path);
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    setPdfUrl(`${baseUrl}/files/uploads/${tmpl.file_path}`);
    const initialFields = {};
    tmpl.text_fields?.forEach(f => initialFields[f.label] = "");
    setTemplateFieldValues(initialFields);
    setRecipients([{ name: '', email: '', phone: '', signature_type: 'e-signature', role_labels: [], selectedContactId: null }]);
  }, [initialTemplateId, templates]);

  const handleTemplateSelect = (e) => {
    const tId = e.target.value;
    if (!tId) {
      setSelectedTemplate(null);
      setPdfUrl('');
      return;
    }
    const tmpl = templates.find(t => t._id === tId);
    setSelectedTemplate(tmpl);
    // For templates, pdfFile is the path string, not a File object
    setPdfFile(tmpl.file_path);

    // Load PDF for Preview (Remote URL)
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    setPdfUrl(`${baseUrl}/files/uploads/${tmpl.file_path}`);

    const initialFields = {};
    tmpl.text_fields?.forEach(f => initialFields[f.label] = "");
    setTemplateFieldValues(initialFields);

    // Reset recipients
    setRecipients([{ name: '', email: '', phone: '', signature_type: 'e-signature', role_labels: [], selectedContactId: null }]);
  };

  const handleFocusField = (label) => {
    setHighlightedItem({ type: 'text', label });
    // Auto-navigate to page
    const field = selectedTemplate?.text_fields.find(f => f.label === label);
    if (field) setPreviewPageNumber(field.page);
  };

  const handleHoverRole = (label) => {
    setHighlightedItem({ type: 'role', label });
    // Auto-navigate to first page where this role appears
    const role = selectedTemplate?.roles.find(r => r.label === label);
    if (role) setPreviewPageNumber(role.page);
  };

  const handleExit = () => {
    if (onExit) {
      onExit();
    } else {
      navigate('/dashboard');
    }
  };

  // UPDATED: Use Local Object URL for Preview instead of Uploading immediately
  const handleFileUpload = (e) => {
    if (isSuperAdmin && !targetOrgId) {
      setErrorMessage("Please select a specific company from the dropdown at the top-right of the page before uploading a document.");
      e.target.value = null;
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    // Clear template state when uploading a file
    setSelectedTemplate(null);
    setTemplateFieldValues({});
    setUseTemplate(false);
    // Clear role_labels from all recipients when uploading
    setRecipients(recipients.map(r => ({ ...r, role_labels: [] })));

    setPdfFile(file); // Store the actual File object
    const localUrl = URL.createObjectURL(file);
    setPdfUrl(localUrl);
    setErrorMessage('');

    if (recipients.length > 0) {
      setSelectedRecipientEmail(recipients[0].email);
    }
  };

  // --- NEW VALIDATION HELPERS ---
  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };

  const handleNextStep = () => {
    setErrorMessage('');
    
    if (isSuperAdmin && !targetOrgId) {
      setErrorMessage("Please select a specific company from the top-right dropdown to proceed.");
      return;
    }
    if (!pdfFile && !selectedTemplate) {
      setErrorMessage("Please upload a document or select a template.");
      return;
    }
    if (enableExpiry && !expiryDate) {
      setErrorMessage("Please select an expiry date.");
      return;
    }

    // Skip recipient validation for public signing link
    if (openLinkRequest) {
      if (useTemplate && selectedTemplate) {
        handleSaveRequest();
      } else {
        setStep(2);
      }
      return;
    }

    // Validate recipients
    for (let i = 0; i < recipients.length; i++) {
      const r = recipients[i];
      if (!r.name || !r.email || !r.phone) {
        setErrorMessage(`Please fill all details for recipient ${i + 1}.`);
        return;
      }
      if (!validateEmail(r.email)) {
        setErrorMessage(`Invalid email format for recipient ${i + 1}. Please ensure it contains @ and .com (or valid domain).`);
        return;
      }
      if (r.phone.length !== 10) {
        setErrorMessage(`Phone number for recipient ${i + 1} must be exactly 10 digits.`);
        return;
      }
      // Template Validation: Must have at least one role ONLY when using template
      if (useTemplate && selectedTemplate && r.role_labels.length === 0) {
        setErrorMessage(`Please assign at least one Role to recipient ${i + 1}.`);
        return;
      }
    }

    if (useTemplate) {
      handleSaveRequest();
    } else {
      setStep(2);
    }
  }

  // --- MULTI-SELECT LOGIC ---
  const toggleRole = (recipientIndex, roleName) => {
    const updated = [...recipients];
    const currentRoles = updated[recipientIndex].role_labels || [];

    if (currentRoles.includes(roleName)) {
      updated[recipientIndex].role_labels = currentRoles.filter(r => r !== roleName);
    } else {
      updated[recipientIndex].role_labels = [...currentRoles, roleName];
    }
    setRecipients(updated);
  };

  const getAvailableRoles = (currentIndex) => {
    if (!selectedTemplate) return [];
    // Use Set to ensure unique role names from template
    const allUniqueRoles = [...new Set(selectedTemplate.roles.map(r => r.label))];

    const assignedToOthers = recipients.flatMap((r, idx) =>
      idx !== currentIndex ? r.role_labels : []
    );

    // Returns all roles that are NOT taken by others.
    return allUniqueRoles.filter(role => !assignedToOthers.includes(role));
  };

  const handleAddSignatureClick = () => {
    setSelectingPages(true);
    setTempSelectedPages([]);
  };

  const handleRecipientChange = (index, field, value) => {
    const updated = recipients.map((r, i) => {
      if (i !== index) return r;
      const next = { ...r };
      next[field] = field === 'phone' ? value.replace(/\D/g, '') : value;
      if (field === 'name' || field === 'email' || field === 'phone') {
        next.selectedContactId = null;
      }
      return next;
    });
    setRecipients(updated);
  };

  const addRecipient = () => {
    setRecipients([...recipients, { name: '', email: '', phone: '', signature_type: 'e-signature', role_labels: [], selectedContactId: null }]);
  };

  const removeRecipient = (index) => {
    const updated = [...recipients];
    updated.splice(index, 1);
    setRecipients(updated);
  };

  const applyContactToRecipient = (recipientIndex, contact) => {
    if (!contact) return;
    setRecipients((prev) => {
      const next = [...prev];
      const r = { ...next[recipientIndex] };
      r.name = contact.name || '';
      r.email = contact.email || '';
      r.phone = (contact.phone || '').replace(/\D/g, '').slice(0, 10);
      r.selectedContactId = contact._id || null;
      next[recipientIndex] = r;
      return next;
    });
  };

  // --- MANUAL PLACEMENT LOGIC (Original Step 2) ---
  const handleConfirmPages = () => {
    const positionKey = openLinkRequest ? "__open_link__" : selectedRecipientEmail;
    if (!openLinkRequest && !selectedRecipientEmail) {
      alert("Please select a recipient before placing signature boxes.");
      return;
    }

    setSelectedPages(tempSelectedPages);
    setSelectingPages(false);

    const newSigPositions = { ...sigPositions };

    tempSelectedPages.forEach(page => {
      if (!newSigPositions[page]) newSigPositions[page] = {};
      newSigPositions[page][positionKey] = { x: 100, y: 100 };
    });

    setSigPositions(newSigPositions);
    setShowSignatureBox(true);
  };

  const handleCancelPageSelection = () => {
    setSelectingPages(false);
  };

  const handleSelectAllPages = () => {
    if (!numPages) return;
    setTempSelectedPages(Array.from({ length: numPages }, (_, idx) => idx + 1));
  };

  const handleClearPageSelection = () => {
    setTempSelectedPages([]);
  };

  const togglePageSelection = (pageNum) => {
    if (tempSelectedPages.includes(pageNum)) {
      setTempSelectedPages(tempSelectedPages.filter(p => p !== pageNum));
    } else {
      setTempSelectedPages([...tempSelectedPages, pageNum]);
    }
  };

  const handleApplyAllPagesChange = () => {
    const newApplyAll = !applyAllPages;
    setApplyAllPages(newApplyAll);
    const positionKey = openLinkRequest ? "__open_link__" : selectedRecipientEmail;

    if (newApplyAll && numPages) {
      const allPages = Array.from({ length: numPages }, (_, idx) => idx + 1);
      setSelectedPages(allPages);
      const newSigPositions = { ...sigPositions };

      allPages.forEach(page => {
        if (!newSigPositions[page]) newSigPositions[page] = {};
        newSigPositions[page][positionKey] = { x: 100, y: 100 };
      });

      setSigPositions(newSigPositions);
      setShowSignatureBox(true);
    }
  };

  // Helper function to validate all recipients have signature boxes placed
  const validateAllRecipientsHaveBoxes = () => {
    if (openLinkRequest) {
      if (useTemplate) return true;
      // Manual open link: require at least one __open_link__ position
      let hasOpenLinkPosition = false;
      if (sigPositions && typeof sigPositions === 'object') {
        Object.values(sigPositions).forEach(pagePositions => {
          if (pagePositions && pagePositions["__open_link__"]) hasOpenLinkPosition = true;
        });
      }
      if (!hasOpenLinkPosition) {
        setValidationModalMessage("Please place at least one signature box for the signer.");
        setShowValidationModal(true);
        return false;
      }
      return true;
    }
    if (useTemplate) {
      // For templates, check that all recipients have roles assigned
      for (let i = 0; i < recipients.length; i++) {
        if (!recipients[i].role_labels || recipients[i].role_labels.length === 0) {
          const errorMsg = `Please assign at least one Role to recipient ${i + 1} (${recipients[i].name || recipients[i].email}).`;
          setValidationModalMessage(errorMsg);
          setShowValidationModal(true);
          return false;
        }
      }
      return true;
    } else {
      // For manual upload, check that all recipients have signature positions
      const recipientEmails = recipients
        .filter(r => r.name && r.email && r.phone) // Only check valid recipients
        .map(r => r.email.trim())
        .filter(email => email.length > 0);
      
      if (recipientEmails.length === 0) {
        const errorMsg = "Please add at least one recipient with complete details before placing signature boxes.";
        setValidationModalMessage(errorMsg);
        setShowValidationModal(true);
        return false;
      }
      
      // Collect all recipient emails that have signature boxes placed
      // sigPositions structure: { pageNumber: { email: { x, y } } }
      const allSigPositions = [];
      
      if (sigPositions && typeof sigPositions === 'object') {
        Object.values(sigPositions).forEach(pagePositions => {
          if (pagePositions && typeof pagePositions === 'object') {
            Object.keys(pagePositions).forEach(email => {
              if (email && email.trim()) {
                allSigPositions.push(email.trim());
              }
            });
          }
        });
      }
      
      const uniqueSigPositions = [...new Set(allSigPositions)];
      
      // Check if all recipient emails have signature boxes
      const missingRecipients = recipientEmails.filter(email => {
        const trimmedEmail = email.trim();
        const hasBox = uniqueSigPositions.includes(trimmedEmail);
        return !hasBox;
      });
      
      if (missingRecipients.length > 0) {
        const missingNames = missingRecipients.map(email => {
          const recipient = recipients.find(r => r.email && r.email.trim() === email.trim());
          return recipient?.name || email;
        }).join(', ');
        
        const recipientCount = missingRecipients.length === 1 ? 'recipient' : 'recipients';
        const errorMsg = `Please place signature boxes for all recipients before sending the request. Missing signature boxes for ${recipientCount}: ${missingNames}`;
        setValidationModalMessage(errorMsg);
        setShowValidationModal(true);
        return false;
      }
      
      // Additional check: ensure at least one page has signature positions
      if (uniqueSigPositions.length === 0) {
        const errorMsg = "Please place at least one signature box before sending the request.";
        setValidationModalMessage(errorMsg);
        setShowValidationModal(true);
        return false;
      }
      
      return true;
    }
  };

  // UPDATED: Submit using FormData for single-step upload
  const handleSaveRequest = (e) => {
    // Prevent default if event is passed
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Clear any previous error messages and modals
    setErrorMessage('');
    setShowValidationModal(false);
    setValidationModalMessage('');
    
    // Validate that all recipients have signature boxes/roles assigned
    const isValid = validateAllRecipientsHaveBoxes();
    
    if (!isValid) {
      // Validation failed - modal is already shown by validateAllRecipientsHaveBoxes
      return;
    }
    
    // Double-check: ensure we have recipients (unless open link)
    if (!openLinkRequest && (!recipients || recipients.length === 0)) {
      setErrorMessage("Please add at least one recipient before sending the request.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setIsSaving(true);
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];

    const jsonPayload = {
      recipients: openLinkRequest ? [] : recipients.map(({ selectedContactId, ...r }) => r),
      open_link: openLinkRequest,
      // Template or pre-uploaded file (string path) → send as file_path; File object → sent via FormData
      file_path: (useTemplate || typeof pdfFile === 'string') ? pdfFile : null,
      sigPositions: sigPositions,
      selected_pages: selectedPages.length ? selectedPages : [1],
      apply_all_pages: applyAllPages,
      rendered_page_width: renderedPageWidth,
      orgId: targetOrgId,
      security_settings: {
        capture_location: captureLocation,
        reminder_enabled: enableReminder,
        reminder_duration: enableReminder ? reminderDuration : null,
        expiry_enabled: enableExpiry,
        expiry_date: enableExpiry ? expiryDate : null,
        face_capture: captureFace,
        email_notification_enabled: emailNotificationEnabled,
        otp_enabled: otpEnabled // NEW FIELD
      }
    };

    if (useTemplate && selectedTemplate) {
      jsonPayload.template_id = selectedTemplate._id;
      jsonPayload.field_values = templateFieldValues;
    }

    const formData = new FormData();
    // Append the JSON data as a string
    formData.append('data', JSON.stringify(jsonPayload));

    // Append file if it is an object (User Upload flow)
    if (!useTemplate && pdfFile && typeof pdfFile === 'object') {
      formData.append('file', pdfFile);
    }

    axios.post(`${baseUrl}/requests/create_request`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
      .then(() => {
        setIsSaving(false);
        setErrorMessage('');
        setStep(3);
      })
      .catch(error => {
        console.error('Error creating request:', error);
        setIsSaving(false);
        setErrorMessage(error.response?.data?.error || "Failed to create request. Please try again.");
      });
  };

  // ── WIZARD HANDLERS ─────────────────────────────────────────────────────
  const handleWizardFileSelect = async (e) => {
    if (isSuperAdmin && !targetOrgId) {
      setWizardError("Please select a company from the top-right dropdown before uploading a document.");
      e.target.value = null;
      return;
    }
    const file = e.target.files[0];
    if (!file) return;
    setWizardFile(file);
    setWizardError('');
    setWizardUploading(true);
    setWizardUploadedPath('');
    setWizardUploadedUrl('');
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${baseUrl}/files/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setWizardUploadedPath(res.data.file_path);
      setWizardUploadedUrl(res.data.public_url);
    } catch (err) {
      // Server upload failed — fall back to local file object
      // The PDF will be uploaded together with the request submission
      setWizardUploadedPath('__local__');
      setWizardUploadedUrl(URL.createObjectURL(file));
    } finally {
      setWizardUploading(false);
    }
  };

  const applyWizardAndClose = () => {
    if (!useTemplate && wizardUploadedPath) {
      if (wizardUploadedPath === '__local__') {
        // Fallback: file wasn't pre-uploaded; use File object (uploaded with request)
        setPdfFile(wizardFile);
      } else {
        // File was pre-uploaded; use server path
        setPdfFile(wizardUploadedPath);
      }
      setPdfUrl(wizardUploadedUrl);
      setSelectedTemplate(null);
      setTemplateFieldValues({});
      setRecipients(recipients.map(r => ({ ...r, role_labels: [] })));
    }
    setWizardActive(false);
  };

  const reopenWizard = () => {
    setWizardStep(1);
    setWizardActive(true);
  };

  // Internal styles (UPDATED for Black & Yellow Theme)
  const customStyles = `
    .initiate-container {
      max-width: 1400px;
      margin: 0 auto;
      display: flex;
      gap: 30px;
    }
    /* LEFT PANEL Logic */
    .left-panel {
        flex: 1;
        max-width: ${useTemplate && selectedTemplate ? '50%' : '100%'};
        transition: all 0.3s ease;
        margin: 0 auto;
    }
    /* RIGHT PANEL Logic (Preview) - dark theme */
    .right-preview-panel {
        flex: 1;
        background: var(--black-bg-card-2);
        padding: 20px;
        border-radius: 12px;
        border: 1px solid var(--black-border);
        display: flex;
        flex-direction: column;
        align-items: center;
        max-height: 85vh;
        overflow-y: auto;
        position: sticky;
        top: 20px;
    }

    .compact-upload-zone {
      position: relative;
      border: 2px dashed var(--black-border-light);
      border-radius: 8px;
      padding: 40px;
      text-align: center;
      background: var(--black-bg-card-4);
      cursor: pointer;
      transition: all 0.2s;
      color: var(--text-on-black-muted);
    }
    .compact-upload-zone:hover {
      background: var(--black-bg-card-3);
      border-color: var(--primary);
    }
    .compact-input-row {
      display: grid;
      grid-template-columns: 20px 1.5fr 1.5fr 1.2fr 1.5fr 30px;
      gap: 10px;
      align-items: center;
    }
    .recipient-input {
      width: 100%;
      padding: 10px;
      border: 1px solid var(--black-border-light);
      border-radius: 6px;
      font-size: 14px;
      background: var(--black-bg-card-4);
      color: var(--text-on-black);
    }
    .phone-input-group {
      display: flex;
      align-items: center;
      background: var(--black-bg-card-4);
      border: 1px solid var(--black-border-light);
      border-radius: 6px;
      overflow: hidden;
    }
    .phone-prefix {
      background: var(--black-bg-card-3);
      color: var(--text-on-black-muted);
      padding: 0 8px;
      font-size: 13px;
      font-weight: 600;
      height: 38px;
      display: flex;
      align-items: center;
      border-right: 1px solid var(--black-border);
    }
    .phone-field {
      border: none !important;
      outline: none !important;
      background: transparent !important;
      color: var(--text-on-black) !important;
    }
    .switch-container {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 15px;
      background: var(--black-bg-card-4);
      border-radius: 8px;
      border: 1px solid var(--black-border);
      transition: all 0.2s;
    }
    .switch-container:hover {
      border-color: var(--black-border-light);
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    }
    .switch {
      position: relative;
      display: inline-block;
      width: 44px;
      height: 24px;
      flex-shrink: 0;
    }
    .switch input {
      opacity: 0;
      width: 0;
      height: 0;
    }
    .slider {
      position: absolute;
      cursor: pointer;
      top: 0; left: 0; right: 0; bottom: 0;
      background-color: var(--black-border-light);
      transition: .4s;
      border-radius: 24px;
    }
    .slider:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: var(--text-on-black);
      transition: .4s;
      border-radius: 50%;
      box-shadow: 0 1px 2px rgba(0,0,0,0.3);
    }
    input:checked + .slider {
      background-color: var(--primary);
    }
    input:checked + .slider:before {
      transform: translateX(20px);
    }
    .config-label {
      font-weight: 500;
      color: var(--text-on-black-secondary);
      display: flex;
      align-items: center;
      gap: 8px;
      white-space: nowrap;
    }

    .signing-method-row {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
      margin-bottom: 24px;
    }
    .signing-method-option {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      padding: 10px 12px;
      border-radius: 10px;
      border: 1px solid var(--black-border);
      background: var(--black-bg-card-3);
      color: var(--text-on-black);
      min-width: 280px;
      max-width: 420px;
    }
    .signing-method-option.active {
      border-color: var(--primary);
      background: rgba(255, 214, 0, 0.12);
      box-shadow: 0 0 0 1px rgba(255, 214, 0, 0.15) inset;
    }
    .signing-method-label {
      font-weight: 600;
      line-height: 1.2;
    }
    .signing-method-sub {
      font-size: 12px;
      color: var(--text-on-black-muted);
      line-height: 1.2;
    }

    /* Multi Select Styles - dark theme */
    .multi-select-container { position: relative; width: 100%; }
    .multi-select-trigger {
       border: 1px solid var(--black-border-light);
       padding: 8px;
       border-radius: 6px;
       font-size: 14px;
       background: var(--black-bg-card-4);
       color: var(--text-on-black);
       cursor: pointer;
       display: flex;
       justify-content: space-between;
       align-items: center;
       min-height: 42px;
    }
    .multi-select-dropdown {
       position: absolute;
       top: 100%;
       left: 0;
       width: 100%;
       background: var(--black-bg-card-3);
       border: 1px solid var(--black-border);
       border-radius: 6px;
       box-shadow: 0 4px 20px rgba(0,0,0,0.5);
       z-index: 50;
       max-height: 200px;
       overflow-y: auto;
       margin-top: 4px;
    }
    .role-option {
       padding: 8px 12px;
       cursor: pointer;
       display: flex;
       align-items: center;
       gap: 8px;
       font-size: 13px;
       color: var(--text-on-black);
    }
    .role-option:hover { background: var(--black-bg-card-4); }
    .role-tag {
       background: rgba(255, 214, 0, 0.2);
       color: var(--primary);
       padding: 2px 6px;
       border-radius: 4px;
       font-size: 11px;
       margin-right: 4px;
       border: 1px solid rgba(255, 214, 0, 0.4);
    }

    /* Spinner */
    .btn-spinner {
      width: 16px; height: 16px; border: 2px solid #000; border-bottom-color: transparent;
      border-radius: 50%; display: inline-block; animation: rotation 1s linear infinite; margin-right: 8px;
    }
    @keyframes rotation { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

    /* Highlight Animations for Preview (Updated to Yellow) */
    .highlight-box { animation: pulse 1.5s infinite; box-shadow: 0 0 15px rgba(255, 214, 0, 0.6); z-index: 100; }
    @keyframes pulse { 0% { transform: scale(1); opacity: 0.7; } 50% { transform: scale(1.05); opacity: 1; } 100% { transform: scale(1); opacity: 0.7; } }

    /* Step 2 alignment + theme-safe styling */
    .step-container-flex {
      background: var(--black-bg-card-2) !important;
      border: 1px solid var(--black-border) !important;
      box-shadow: 0 6px 20px rgba(0,0,0,0.25) !important;
      max-width: 1280px !important;
      margin: 0 auto !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) 300px;
      gap: 24px !important;
      align-items: start !important;
      padding: 24px !important;
    }
    .pdf-review-container {
      margin-top: 0 !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 12px !important;
      min-width: 0;
    }
    .pdf-side-controls {
      background: var(--black-bg-card-3) !important;
      border: 1px solid var(--black-border) !important;
      border-radius: 12px !important;
      padding: 18px !important;
      width: 100%;
      min-width: 0;
    }
    .pdf-side-controls .checkbox-control {
      display: flex !important;
      align-items: center !important;
      gap: 10px !important;
      margin-top: 12px !important;
      font-size: 15px !important;
      color: var(--text-on-black);
    }
    .pdf-side-controls .checkbox-control label {
      margin: 0 !important;
      color: var(--text-on-black-secondary) !important;
      font-size: 15px !important;
      line-height: 1.25;
      cursor: pointer;
    }
    .pdf-side-controls .checkbox-control input[type="checkbox"] {
      width: 16px;
      height: 16px;
      margin: 0 !important;
      accent-color: var(--primary);
      flex-shrink: 0;
    }
    .control-inline-options {
      display: flex;
      align-items: center;
      gap: 18px;
      flex-wrap: wrap;
      width: 100%;
      justify-content: flex-start;
      margin-top: 8px;
    }
    .pdf-preview-shell {
      position: relative;
      width: fit-content;
      margin: 0 auto;
    }
    .pdf-nav-side {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 20;
      min-width: 70px;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      padding: 8px 10px;
    }
    .pdf-nav-side.prev {
      left: -88px;
    }
    .pdf-nav-side.next {
      right: -88px;
    }
    .pdf-page-indicator {
      margin-top: 10px;
      text-align: center;
      color: var(--text-on-black);
      font-size: 16px;
      font-weight: 700;
    }
    .page-info {
      color: var(--text-on-black);
      text-align: center;
      white-space: nowrap;
      font-size: 18px !important;
      font-weight: 700 !important;
    }
    .pdf-side-controls .button-group {
      width: 100%;
      margin-top: 22px !important;
    }
    .pdf-side-controls .button-group .btn {
      width: 100%;
      justify-content: center;
    }

    .page-picker-modal {
      max-width: 860px !important;
      width: min(92vw, 860px) !important;
      max-height: 82vh !important;
      display: flex;
      flex-direction: column;
      padding: 0 !important;
      overflow: hidden;
    }
    .page-picker-header {
      padding: 18px 22px 12px;
      border-bottom: 1px solid var(--black-border);
      background: var(--black-bg-card-2);
      position: sticky;
      top: 0;
      z-index: 2;
    }
    .page-picker-top-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;
    }
    .page-picker-top-row h3 {
      margin: 0;
      font-size: 18px;
      color: var(--text-on-black);
    }
    .page-picker-count {
      font-size: 12px;
      color: var(--text-on-black-muted);
      background: var(--black-bg-card-4);
      border: 1px solid var(--black-border);
      border-radius: 999px;
      padding: 4px 10px;
    }
    .page-picker-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
    .page-chip-btn {
      border: 1px solid var(--black-border-light);
      background: var(--black-bg-card-4);
      color: var(--text-on-black-secondary);
      border-radius: 8px;
      padding: 6px 10px;
      font-size: 12px;
      cursor: pointer;
    }
    .page-chip-btn:hover {
      border-color: var(--black-border-subtle);
      background: var(--black-bg-card-3);
      color: var(--text-on-black);
    }
    .page-picker-grid-wrap {
      padding: 14px 22px 8px;
      overflow-y: auto;
      flex: 1;
      min-height: 220px;
    }
    .page-picker-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
      gap: 10px;
    }
    .page-picker-item {
      border: 1px solid var(--black-border);
      background: var(--black-bg-card-4);
      border-radius: 10px;
      padding: 9px 10px;
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--text-on-black);
      font-size: 14px;
      cursor: pointer;
      user-select: none;
    }
    .page-picker-item:hover {
      border-color: var(--primary);
      box-shadow: 0 0 0 1px rgba(255,214,0,0.16) inset;
    }
    .page-picker-item input {
      margin: 0;
      accent-color: var(--primary);
    }
    .page-picker-footer {
      border-top: 1px solid var(--black-border);
      padding: 14px 22px 18px;
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      background: var(--black-bg-card-2);
    }

    @media (max-width: 1024px) {
      .step-container-flex {
        grid-template-columns: 1fr !important;
      }
    }

    /* ── Setup Wizard ──────────────────────────────────────── */
    .wizard-overlay {
      position: fixed; top: 0; left: 260px; right: 0; bottom: 0;
      background: rgba(0,0,0,0.92);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      display: flex; align-items: center; justify-content: center;
      z-index: 1800; padding: 20px;
    }
    @media (max-width: 768px) {
      .wizard-overlay { left: 0; }
    }
    .wizard-card {
      background: var(--black-bg-card-1);
      border: 1px solid var(--black-border);
      border-radius: 16px;
      width: 100%; max-width: 560px;
      overflow: hidden;
      box-shadow: 0 24px 60px rgba(0,0,0,0.5);
      animation: wizardIn 0.25s ease;
    }
    @keyframes wizardIn {
      from { opacity: 0; transform: scale(0.95) translateY(10px); }
      to   { opacity: 1; transform: scale(1) translateY(0); }
    }
    .wizard-header {
      padding: 24px 28px 20px;
      border-bottom: 1px solid var(--black-border);
      display: flex; align-items: center; justify-content: space-between;
    }
    .wizard-title {
      font-size: 18px; font-weight: 700; color: var(--text-on-black); margin: 0;
    }
    .wizard-step-dots {
      display: flex; gap: 6px;
    }
    .wizard-dot {
      width: 8px; height: 8px; border-radius: 50%;
      background: var(--black-border-light); transition: background 0.2s;
    }
    .wizard-dot.active { background: var(--primary); }
    .wizard-dot.done   { background: #10B981; }
    .wizard-body { padding: 28px; }
    .wizard-footer {
      padding: 16px 28px 24px;
      display: flex; justify-content: space-between; align-items: center;
      border-top: 1px solid var(--black-border);
    }

    .wizard-choice-grid {
      display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
    }
    .wizard-choice-card {
      padding: 22px 18px; border-radius: 12px;
      border: 2px solid var(--black-border);
      background: var(--black-bg-card-2);
      cursor: pointer; text-align: center;
      transition: all 0.2s; color: var(--text-on-black);
    }
    .wizard-choice-card:hover {
      border-color: var(--primary);
      background: rgba(255,214,0,0.06);
    }
    .wizard-choice-card.selected {
      border-color: var(--primary);
      background: rgba(255,214,0,0.1);
      box-shadow: 0 0 0 1px rgba(255,214,0,0.2) inset;
    }
    .wizard-choice-icon {
      font-size: 32px; margin-bottom: 10px;
      display: flex; align-items: center; justify-content: center;
      color: var(--primary);
    }
    .wizard-choice-title { font-size: 15px; font-weight: 700; margin-bottom: 4px; }
    .wizard-choice-sub   { font-size: 12px; color: var(--text-on-black-muted); line-height: 1.4; }

    .wizard-summary-bar {
      display: flex; gap: 10px; flex-wrap: wrap;
      padding: 10px 14px; border-radius: 8px;
      background: var(--black-bg-card-3); border: 1px solid var(--black-border);
      margin-bottom: 20px; align-items: center; justify-content: space-between;
    }
    .wizard-summary-tags { display: flex; gap: 8px; flex-wrap: wrap; }
    .wizard-tag {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600;
      background: rgba(255,214,0,0.12); color: var(--primary); border: 1px solid rgba(255,214,0,0.3);
    }
    .wizard-edit-btn {
      background: none; border: 1px solid var(--black-border); border-radius: 6px;
      color: var(--text-on-black-muted); font-size: 12px; padding: 4px 10px; cursor: pointer;
      transition: all 0.2s; white-space: nowrap;
    }
    .wizard-edit-btn:hover { border-color: var(--primary); color: var(--primary); }

    .wizard-upload-zone {
      border: 2px solid var(--black-border-light); border-radius: 10px;
      padding: 40px 24px; text-align: center; cursor: pointer;
      background: var(--black-bg-card-3); transition: all 0.2s;
      color: var(--text-on-black-muted); position: relative;
    }
    .wizard-upload-zone:hover { border-color: var(--black-border-subtle); background: var(--black-bg-card-4); }
    .wizard-upload-zone.has-file { border-color: #10B981; background: rgba(16,185,129,0.05); }
    @keyframes spin { to { transform: rotate(360deg); } }
  `;

  return (
    <div className="App" onClick={() => setActiveRoleDropdown(null)}>
      <style>{customStyles}</style>

      {/* ── SETUP WIZARD OVERLAY ─────────────────────────────────────────── */}
      {wizardActive && step === 1 && (
        <div className="wizard-overlay" onClick={e => e.stopPropagation()}>
          <div className="wizard-card">
            {/* Wizard header */}
            <div className="wizard-header">
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-on-black-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                  Step {wizardStep} of 3
                </div>
                <p className="wizard-title">
                  {wizardStep === 1 && 'How would you like to send?'}
                  {wizardStep === 2 && (useTemplate ? 'Select a Template' : 'Upload Your Document')}
                  {wizardStep === 3 && 'Who will sign?'}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="wizard-step-dots">
                  {[1, 2, 3].map(s => (
                    <div key={s} className={`wizard-dot ${s < wizardStep ? 'done' : s === wizardStep ? 'active' : ''}`} />
                  ))}
                </div>
                <button
                  onClick={() => { setWizardActive(false); onExit?.(); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-on-black-muted)', display: 'flex', alignItems: 'center', padding: 4, borderRadius: 4 }}
                  title="Close"
                >
                  <FiX size={18} />
                </button>
              </div>
            </div>

            {/* Wizard body */}
            <div className="wizard-body">

              {/* Step 1: Document source */}
              {wizardStep === 1 && (
                <div>
                  <p style={{ color: 'var(--text-on-black-muted)', fontSize: 13, marginBottom: 20, marginTop: 0 }}>
                    Choose how you want to set up this signing request.
                  </p>
                  <div className="wizard-choice-grid">
                    <div className={`wizard-choice-card ${!useTemplate ? 'selected' : ''}`} onClick={() => { setUseTemplate(false); setSelectedTemplate(null); setTemplateFieldValues({}); }}>
                      <div className="wizard-choice-icon"><FiUploadCloud /></div>
                      <div className="wizard-choice-title">Upload Document</div>
                      <div className="wizard-choice-sub">Upload a PDF and place signature areas manually</div>
                    </div>
                    <div className={`wizard-choice-card ${useTemplate ? 'selected' : ''}`} onClick={() => setUseTemplate(true)}>
                      <div className="wizard-choice-icon"><FiLayout /></div>
                      <div className="wizard-choice-title">Use Template</div>
                      <div className="wizard-choice-sub">Send a pre-configured document with signature fields already placed</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2a: Upload PDF */}
              {wizardStep === 2 && !useTemplate && (
                <div>
                  <p style={{ color: 'var(--text-on-black-muted)', fontSize: 13, marginBottom: 16, marginTop: 0 }}>
                    Upload the PDF that recipients will sign.
                  </p>
                  <label className={`wizard-upload-zone ${wizardUploadedPath ? 'has-file' : ''}`}
                    style={{ cursor: wizardUploading ? 'not-allowed' : 'pointer' }}>
                    <input type="file" accept="application/pdf" style={{ display: 'none' }}
                      onChange={handleWizardFileSelect} disabled={wizardUploading} />

                    {wizardUploading ? (
                      <div>
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ width: 36, height: 36, border: '3px solid var(--primary)', borderTopColor: 'transparent',
                            borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>Uploading {wizardFile?.name}…</div>
                        <div style={{ fontSize: 12, marginTop: 6, color: 'var(--text-on-black-muted)' }}>Please wait</div>
                      </div>
                    ) : wizardUploadedPath ? (
                      <div>
                        <div style={{ fontSize: 36, marginBottom: 10, color: '#10B981' }}><FiCheck /></div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: '#10B981', marginBottom: 4 }}>{wizardFile?.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-on-black-muted)', marginBottom: 10 }}>
                          {wizardFile ? (wizardFile.size > 1024*1024 ? `${(wizardFile.size/1024/1024).toFixed(1)} MB` : `${Math.round(wizardFile.size/1024)} KB`) : ''} · {wizardUploadedPath === '__local__' ? 'Ready' : 'Uploaded successfully'}
                        </div>
                        <div style={{ fontSize: 11, padding: '4px 12px', borderRadius: 20,
                          background: 'rgba(16,185,129,0.15)', color: '#10B981', display: 'inline-block' }}>
                          ✓ Ready — click to replace
                        </div>
                      </div>
                    ) : (
                      <div>
                        <FiUploadCloud size={40} style={{ marginBottom: 14, opacity: 0.45 }} />
                        <div style={{ fontWeight: 700, fontSize: 15 }}>Click to select a PDF file</div>
                        <div style={{ fontSize: 12, marginTop: 6, color: 'var(--text-on-black-muted)' }}>PDF format only · Max 100 MB</div>
                      </div>
                    )}
                  </label>
                  {wizardError && (
                    <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8,
                      background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                      color: '#ef4444', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FiAlertTriangle size={14} /> {wizardError}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2b: Select Template */}
              {wizardStep === 2 && useTemplate && (
                <div>
                  <p style={{ color: 'var(--text-on-black-muted)', fontSize: 13, marginBottom: 16, marginTop: 0 }}>
                    Choose a template — signature positions are predefined. You'll fill in document fields after setup.
                  </p>
                  <select
                    className="recipient-input"
                    style={{ padding: 12 }}
                    value={selectedTemplate?._id || ''}
                    onChange={handleTemplateSelect}
                  >
                    <option value="">-- Choose a Template --</option>
                    {templates.map(t => <option key={t._id} value={t._id}>{t.title}</option>)}
                  </select>
                </div>
              )}

              {/* Step 3: Signing method */}
              {wizardStep === 3 && (
                <div>
                  <p style={{ color: 'var(--text-on-black-muted)', fontSize: 13, marginBottom: 20, marginTop: 0 }}>
                    Choose who can sign this document.
                  </p>
                  <div className="wizard-choice-grid">
                    <div className={`wizard-choice-card ${!openLinkRequest ? 'selected' : ''}`} onClick={() => setOpenLinkRequest(false)}>
                      <div className="wizard-choice-icon" style={{ fontSize: 28 }}>👥</div>
                      <div className="wizard-choice-title">Specific Recipients</div>
                      <div className="wizard-choice-sub">Add named recipients — each gets a unique signing link</div>
                    </div>
                    <div className={`wizard-choice-card ${openLinkRequest ? 'selected' : ''}`} onClick={() => setOpenLinkRequest(true)}>
                      <div className="wizard-choice-icon" style={{ fontSize: 28 }}>🔗</div>
                      <div className="wizard-choice-title">Public Link</div>
                      <div className="wizard-choice-sub">Anyone with the URL can open and sign the document</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Wizard footer — error banner + navigation */}
            {wizardError && wizardStep !== 2 && (
              <div style={{ margin: '0 28px 0', padding: '10px 14px', borderRadius: 8,
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                color: '#ef4444', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiAlertTriangle size={14} /> {wizardError}
              </div>
            )}
            <div className="wizard-footer">
              {wizardStep === 1 ? (
                <button className="btn btn-secondary" onClick={() => { setWizardActive(false); onExit?.(); }}>← Cancel</button>
              ) : (
                <button className="btn btn-secondary" onClick={() => { setWizardStep(wizardStep - 1); setWizardError(''); }}>← Back</button>
              )}
              <button
                className="btn btn-primary"
                disabled={wizardUploading}
                onClick={() => {
                  if (wizardStep === 1) {
                    setWizardStep(2);
                  } else if (wizardStep === 2) {
                    if (!useTemplate && !wizardUploadedPath) {
                      setWizardError('Please upload a PDF file before continuing.');
                      return;
                    }
                    if (useTemplate && !selectedTemplate) {
                      setWizardError('Please select a template.');
                      return;
                    }
                    setWizardError('');
                    setWizardStep(3);
                  } else if (wizardStep === 3) {
                    applyWizardAndClose();
                  }
                  setWizardError('');
                }}
              >
                {wizardStep === 3 ? 'Done — Configure & Send →' : 'Continue →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="initiate-page-header" style={{ padding: '0 0 20px 0', borderBottom: '1px solid var(--black-border)', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0, color: 'var(--text-on-black)' }}>Initiate New Request</h2>
        {/* Show edit setup button once wizard is done */}
        {!wizardActive && step === 1 && (
          <button className="wizard-edit-btn" onClick={reopenWizard} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            ✏️ Edit Setup
          </button>
        )}
      </div>

      {/* Wizard summary bar — shown after wizard completed */}
      {!wizardActive && step === 1 && (pdfFile || selectedTemplate) && (
        <div className="wizard-summary-bar">
          <div className="wizard-summary-tags">
            <span className="wizard-tag">
              {useTemplate ? <><FiLayout size={11} /> Template: {selectedTemplate?.title || 'Selected'}</> : <><FiUploadCloud size={11} /> {wizardFile?.name || (typeof pdfFile === 'object' ? pdfFile?.name : 'Document uploaded')}</>}
            </span>
            <span className="wizard-tag">
              {openLinkRequest ? '🔗 Public Link' : '👥 Specific Recipients'}
            </span>
          </div>
          <button className="wizard-edit-btn" onClick={reopenWizard}>✏️ Edit</button>
        </div>
      )}

      {/* SUCCESS STEP (STEP 3) */}
      {step === 3 && (
        <div className="step-container" style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: 60, color: '#10B981', marginBottom: 20 }}><FiCheck /></div>
          <h2>Request Sent Successfully!</h2>
          <p style={{ color: '#666', marginBottom: 30 }}>
            The recipients have been notified via email.
          </p>
          <button type="button" className="btn btn-primary" onClick={handleExit}>
            Return to Dashboard
          </button>
        </div>
      )}

      {/* STEP 1: Upload & Config (SPLIT LAYOUT) */}
      {step === 1 && (
        <div className="initiate-container">
          {/* LEFT PANEL */}
          <div className="left-panel">
            
            {/* Custom Error Message Box */}
            {errorMessage && (
              <div style={{
                background: '#FEE2E2',
                border: '1px solid #EF4444',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: '#991B1B'
              }}>
                <FiAlertTriangle size={18} />
                <div style={{ flex: 1 }}>{errorMessage}</div>
                <button 
                  onClick={() => setErrorMessage('')}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    cursor: 'pointer', 
                    color: '#991B1B',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <FiX size={18} />
                </button>
              </div>
            )}

            {/* CONFIGURATION BAR */}
            <div className="config-bar" style={{
              display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '25px',
              padding: '20px', background: 'var(--black-bg-card-2)', borderRadius: '12px', border: '1px solid var(--black-border)'
            }}>
              <div className="switch-container">
                <label className="switch"><input type="checkbox" checked={captureLocation} onChange={(e) => setCaptureLocation(e.target.checked)} /><span className="slider"></span></label>
                <span className="config-label"><FiMapPin /> Location</span>
              </div>
              <div className="switch-container">
                <label className="switch"><input type="checkbox" checked={enableReminder} onChange={(e) => setEnableReminder(e.target.checked)} /><span className="slider"></span></label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="config-label"><FiClock /> Reminder</span>
                  {enableReminder && (
                    <select value={reminderDuration} onChange={(e) => setReminderDuration(e.target.value)} className="input-field" style={{ padding: '2px 6px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: '13px', margin: 0, width: 'auto', height: 28 }}>
                      <option value="12">12h</option><option value="24">24h</option><option value="48">48h</option>
                    </select>
                  )}
                </div>
              </div>
              <div className="switch-container">
                <label className="switch"><input type="checkbox" checked={enableExpiry} onChange={(e) => setEnableExpiry(e.target.checked)} /><span className="slider"></span></label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'nowrap' }}>
                  <span className="config-label"><FiCalendar /> Expiry</span>
                  {enableExpiry && (<input type="date" value={expiryDate} min={new Date().toISOString().split("T")[0]} onChange={(e) => setExpiryDate(e.target.value)} className="input-field" style={{ padding: '2px 6px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: '13px', margin: 0, width: 'auto', height: 28 }} />)}
                </div>
              </div>

              {/* NEW: Face Capture Toggle */}
              <div className="switch-container">
                <label className="switch">
                  <input type="checkbox" checked={captureFace} onChange={(e) => setCaptureFace(e.target.checked)} />
                  <span className="slider"></span>
                </label>
                <span className="config-label"><FiCamera /> Face Capture</span>
              </div>

              {/* NEW: Email Notification Toggle */}
              <div className="switch-container">
                <label className="switch">
                  <input type="checkbox" checked={emailNotificationEnabled} onChange={(e) => setEmailNotificationEnabled(e.target.checked)} />
                  <span className="slider"></span>
                </label>
                <span className="config-label"><FiMail /> Email Notification</span>
              </div>

              {/* NEW: OTP Verification Toggle */}
              <div className="switch-container">
                <label className="switch">
                  <input type="checkbox" checked={otpEnabled} onChange={(e) => setOtpEnabled(e.target.checked)} />
                  <span className="slider"></span>
                </label>
                <span className="config-label"><FiShield /> OTP Verification</span>
              </div>
            </div>

            {/* TEMPLATE SELECTION — show when wizard complete/bypassed and using template */}
            {useTemplate && !wizardActive && (
              <div className="form-group" style={{ marginBottom: 30 }}>
                <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Select Template</label>
                <select
                  onChange={handleTemplateSelect}
                  className="recipient-input"
                  style={{ padding: 12 }}
                  value={selectedTemplate?._id || ""}
                >
                  <option value="">-- Choose a Template --</option>
                  {templates.map(t => <option key={t._id} value={t._id}>{t.title}</option>)}
                </select>
              </div>
            )}

            {/* TEMPLATE FIELDS (Only if useTemplate) */}
            {useTemplate && selectedTemplate && selectedTemplate.text_fields?.length > 0 && (
              <div style={{ marginBottom: 30, padding: 20, background: 'rgba(255, 214, 0, 0.08)', borderRadius: 8, border: '1px solid rgba(255, 214, 0, 0.3)' }}>
                <h4>Document Details</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                  {[...new Set(selectedTemplate.text_fields.map(f => f.label))].map(label => (
                    <div key={label}>
                      <label style={{ fontSize: 12, fontWeight: 600 }}>{label}</label>
                      <input
                        type="text"
                        className="recipient-input"
                        value={templateFieldValues[label] || ""}
                        onFocus={() => handleFocusField(label)}
                        onBlur={() => setHighlightedItem(null)}
                        onChange={e => setTemplateFieldValues({ ...templateFieldValues, [label]: e.target.value })}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Signing method — shown when wizard bypassed (template quick-start), not after normal wizard flow */}
            {!wizardActive && (initialUseTemplate || !!initialTemplateId) && (
              <>
                <h3 style={{ fontSize: 18, marginBottom: 12 }}>Signing method</h3>
                <div className="signing-method-row">
                  <label className={`signing-method-option ${!openLinkRequest ? 'active' : ''}`}>
                    <input type="radio" name="signing-method" checked={!openLinkRequest} onChange={() => setOpenLinkRequest(false)} />
                    <span className="signing-method-label">Specific recipients</span>
                    <span className="signing-method-sub">Recipient-specific signing links</span>
                  </label>
                  <label className={`signing-method-option ${openLinkRequest ? 'active' : ''}`}>
                    <input type="radio" name="signing-method" checked={openLinkRequest} onChange={() => setOpenLinkRequest(true)} />
                    <span className="signing-method-label">Public link</span>
                    <span className="signing-method-sub">Anyone with URL can sign</span>
                  </label>
                </div>
              </>
            )}

            {!openLinkRequest && (
              <>
            <h3 style={{ fontSize: 18, marginBottom: 15 }}>Recipients</h3>
            <div style={{ background: 'var(--black-bg-card-3)', border: '1px solid var(--black-border)', borderRadius: 12, padding: 20, marginBottom: 30 }}>

              <div style={{ display: 'grid', gridTemplateColumns: '20px 140px 1.5fr 1.5fr 1.2fr 1.5fr 30px', gap: 10, marginBottom: 10, paddingLeft: 5 }}>
                <div></div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-on-black-muted)' }}>FROM CONTACTS</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-on-black-muted)' }}>FULL NAME</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-on-black-muted)' }}>EMAIL</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-on-black-muted)' }}>PHONE</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-on-black-muted)' }}>{useTemplate ? "ROLES" : "SIGNATURE TYPE"}</div>
                <div></div>
              </div>

              {recipients.map((r, idx) => (
                <div key={idx} style={{ marginBottom: '10px' }}>
                  <div className="compact-input-row" style={{ gridTemplateColumns: '20px 140px 1.5fr 1.5fr 1.2fr 1.5fr 30px' }}>
                    <span style={{ color: '#64748b', fontWeight: 600, width: 20 }}>{idx + 1}.</span>
                    <select
                      className="recipient-input"
                      style={{ minWidth: 0 }}
                      value={r.selectedContactId || ''}
                      onChange={(e) => {
                        const id = e.target.value;
                        if (!id) return;
                        const contact = contactsList.find(c => c._id === id);
                        if (contact) applyContactToRecipient(idx, contact);
                      }}
                    >
                      <option value="">Select contact</option>
                      {contactsList.map(c => (
                        <option key={c._id} value={c._id}>{c.name} ({c.email})</option>
                      ))}
                    </select>
                    <input className="recipient-input" placeholder="John Doe" value={r.name} onChange={(e) => handleRecipientChange(idx, 'name', e.target.value)} />
                    <input className="recipient-input" placeholder="john@example.com" value={r.email} onChange={(e) => handleRecipientChange(idx, 'email', e.target.value)} />

                    {/* UPDATED: +91 Phone Input UI */}
                    <div className="phone-input-group">
                      <div className="phone-prefix">+91</div>
                      <input
                        className="recipient-input phone-field"
                        placeholder="9876543210"
                        maxLength={10}
                        value={r.phone}
                        onChange={(e) => handleRecipientChange(idx, 'phone', e.target.value)}
                      />
                    </div>

                    {/* CONDITIONAL COLUMN: Multi-Select Roles (Template) OR Sign Type (Manual) */}
                    {useTemplate && selectedTemplate ? (
                      <div className="multi-select-container" onClick={e => e.stopPropagation()}>
                        <div className="multi-select-trigger" onClick={() => setActiveRoleDropdown(activeRoleDropdown === idx ? null : idx)}>
                          {r.role_labels.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {r.role_labels.map(l => <span key={l} className="role-tag">{l}</span>)}
                            </div>
                          ) : <span style={{ color: 'var(--text-on-black-muted)' }}>Select Roles</span>}
                          <FiArrowRight style={{ transform: 'rotate(90deg)', fontSize: 12 }} />
                        </div>
                        {activeRoleDropdown === idx && (
                          <div className="multi-select-dropdown">
                            {getAvailableRoles(idx).map(role => (
                              <div key={role} className="role-option"
                                onMouseEnter={() => handleHoverRole(role)}
                                onMouseLeave={() => setHighlightedItem(null)}
                                onClick={() => toggleRole(idx, role)}>
                                <input type="checkbox" checked={r.role_labels.includes(role)} readOnly />
                                {role}
                              </div>
                            ))}
                            {getAvailableRoles(idx).length === 0 && <div style={{ padding: 10, color: 'var(--text-on-black-muted)', fontSize: 12 }}>No roles available</div>}
                          </div>
                        )}
                      </div>
                    ) : (
                      <select className="recipient-input" value={r.signature_type} onChange={(e) => handleRecipientChange(idx, 'signature_type', e.target.value)}>
                        <option value="e-signature">E-Signature</option>
                        <option value="aadhaar" disabled>Aadhaar (Coming Soon)</option>
                      </select>
                    )}

                    {recipients.length > 1 && (
                      <button
                        type="button"
                        style={{
                          background: 'transparent', border: 'none', color: '#ef4444', fontSize: '20px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', borderRadius: '4px',
                          transition: 'background 0.2s, color 0.2s'
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
                        onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ef4444'; }}
                        title="Remove Recipient"
                        onClick={() => removeRecipient(idx)}
                      >
                        <RiDeleteBinLine />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button type="button" className="btn btn-secondary btn-sm" onClick={addRecipient} style={{ marginTop: 5, fontSize: 13 }}>
                <FiPlus style={{ marginRight: 4 }} /> Add Recipient
              </button>
            </div>
              </>
            )}

            {/* UPLOAD DOCUMENT ZONE — shown only when wizard is done but no file selected */}
            {!useTemplate && !wizardActive && !pdfFile && (
              <>
                <h3 style={{ fontSize: 18, marginBottom: 15 }}>Upload Document</h3>
                <div className="compact-upload-zone">
                  <input type="file" accept="application/pdf" onChange={handleFileUpload} disabled={isUploading}
                    style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', top: 0, left: 0, cursor: 'pointer', zIndex: 10 }}
                  />
                  <div style={{ pointerEvents: 'none' }}>
                    <FiUploadCloud size={32} color="#64748b" />
                    <p style={{ color: '#64748b', margin: '5px 0', fontSize: 14 }}>
                      {pdfFile && pdfFile.name ? <span style={{ color: '#10b981', fontWeight: 600 }}><FiCheck /> {pdfFile.name}</span> : "Click to select a PDF file"}
                    </p>
                    {isUploading && <SigningLoader size="md" label="Uploading..." />}
                  </div>
                </div>
              </>
            )}

            <div style={{ textAlign: 'right', marginTop: 30 }}>
              <button type="button" className="btn btn-primary" onClick={handleNextStep} disabled={(!pdfFile && !selectedTemplate) || isUploading || isSaving} style={{ padding: '10px 24px' }}>
                {isSaving ? (
                  <SigningLoader size="sm" label="" />
                ) : (
                  <> {useTemplate ? "Create & Send Request" : "Next Step"} <FiArrowRight style={{ marginLeft: 5 }} /> </>
                )}
              </button>
            </div>
          </div>

          {/* RIGHT PANEL: LIVE PREVIEW (Only for Template) */}
          {useTemplate && selectedTemplate && (
            <div className="right-preview-panel">
              <div style={{ marginBottom: 10, display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button className="btn btn-sm btn-secondary" disabled={previewPageNumber <= 1} onClick={() => setPreviewPageNumber(previewPageNumber - 1)}>Prev</button>
                <span>Page {previewPageNumber} of {previewNumPages}</span>
                <button className="btn btn-sm btn-secondary" disabled={previewPageNumber >= previewNumPages} onClick={() => setPreviewPageNumber(previewPageNumber + 1)}>Next</button>
              </div>

              <div style={{ position: 'relative', border: '1px solid #ddd', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                {pdfUrl && (
                  <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setPreviewNumPages(numPages)}>
                    <Page pageNumber={previewPageNumber} width={400} renderTextLayer={false} renderAnnotationLayer={false} />
                  </Document>
                )}

                {/* Highlight Text Fields */}
                {selectedTemplate.text_fields.filter(t => t.page === previewPageNumber).map((t, i) => (
                  <div key={`txt-${i}`} style={{
                    position: 'absolute', left: t.x * (400 / renderedPageWidth), top: t.y * (400 / renderedPageWidth),
                    padding: '4px 8px', border: '1px solid orange', fontSize: 10,
                    background: highlightedItem?.type === 'text' && highlightedItem?.label === t.label ? 'rgba(255, 165, 0, 0.5)' : 'rgba(255, 165, 0, 0.1)',
                    transition: 'all 0.3s'
                  }} className={highlightedItem?.type === 'text' && highlightedItem?.label === t.label ? 'highlight-box' : ''}>
                    {templateFieldValues[t.label] || t.label}
                  </div>
                ))}

                {/* Highlight Signatures (Yellow Box for SignTada Theme) */}
                {selectedTemplate.roles.filter(r => r.page === previewPageNumber).map((r, i) => (
                  <div key={`sig-${i}`} style={{
                    position: 'absolute', left: r.x * (400 / renderedPageWidth), top: r.y * (400 / renderedPageWidth),
                    width: 150 * (400 / renderedPageWidth), height: 50 * (400 / renderedPageWidth),
                    border: '2px solid #F57F17', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: highlightedItem?.type === 'role' && highlightedItem?.label === r.label ? 'rgba(255, 214, 0, 0.5)' : 'rgba(255, 214, 0, 0.1)',
                    fontSize: 10, color: 'black', fontWeight: 'bold',
                    transition: 'all 0.3s'
                  }} className={highlightedItem?.type === 'role' && highlightedItem?.label === r.label ? 'highlight-box' : ''}>
                    {r.label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Place Signature (EXISTING MANUAL LOGIC PRESERVED) */}
      {step === 2 && !useTemplate && (
        <div className="step-container-flex">
          {/* Error Message Box for Step 2 */}
          {errorMessage && (
            <div style={{
              position: 'fixed',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              background: '#FEE2E2',
              border: '1px solid #EF4444',
              borderRadius: '8px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: '#991B1B',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              maxWidth: '600px',
              width: '90%'
            }}>
              <FiAlertTriangle size={18} />
              <div style={{ flex: 1, fontWeight: 500 }}>{errorMessage}</div>
              <button 
                onClick={() => setErrorMessage('')}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  color: '#991B1B',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <FiX size={18} />
              </button>
            </div>
          )}
          
          <div className="pdf-review-container">
            {!openLinkRequest && (
            <div className="input-field" style={{ maxWidth: '300px', marginBottom: '10px' }}>
              <label>Select Recipient:</label>
              <select className="input-field" value={selectedRecipientEmail} onChange={(e) => {
                setSelectedRecipientEmail(e.target.value);
                setApplyAllPages(false);
                setDragAllAtOnce(false);
                setShowSignatureBox(true);
              }}>
                {recipients.map((r) => (
                  <option key={r.email} value={r.email}>{r.name} ({r.email})</option>
                ))}
              </select>
            </div>
            )}
            {openLinkRequest && (
              <p style={{ marginBottom: 10, fontSize: 14, color: 'var(--text-on-black-secondary)' }}>Place one signature box where the signer should sign.</p>
            )}

            <div className="pdf-preview-shell">
              <button
                type="button"
                className="btn btn-sm btn-secondary pdf-nav-side prev"
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber(pageNumber - 1)}
              >
                Prev
              </button>
            <div className="pdf-container" style={{
              position: 'relative', width: `${renderedPageWidth}px`, height: 'auto',
              margin: '0 auto', border: '1px solid #e2e8f0', borderRadius: '10px', backgroundColor: '#fff', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>

              {/* Use pdfUrl (Local Blob URL) here */}
              {pdfUrl && (
                <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
                  <Page pageNumber={pageNumber} width={renderedPageWidth} renderTextLayer={false} renderAnnotationLayer={false} />
                </Document>
              )}

              {showSignatureBox && sigPositions[pageNumber] &&
                Object.entries(sigPositions[pageNumber]).map(([email, pos], idx) => (
                  <div key={email}
                    style={{
                      position: 'absolute', top: pos.y, left: pos.x,
                      width: '150px', height: '50px', backgroundColor: 'rgba(255, 214, 0, 0.8)',
                      color: 'black', fontWeight: 'bold', border: '2px solid #b45309', borderRadius: 4,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                      cursor: 'move', userSelect: 'none', zIndex: 100 + idx, boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const initialX = pos.x;
                      const initialY = pos.y;
                      const handleMouseMove = (moveEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                        const deltaY = moveEvent.clientY - startY;
                        const newX = initialX + deltaX;
                        const newY = initialY + deltaY;
                        setSigPositions(prev => {
                          const updated = { ...prev };
                          if (dragAllAtOnce) {
                            Object.keys(updated).forEach(page => {
                              if (updated[page] && updated[page][email]) {
                                updated[page][email] = { x: newX, y: newY };
                              }
                            });
                          } else {
                            if (!updated[pageNumber]) updated[pageNumber] = {};
                            updated[pageNumber][email] = { x: newX, y: newY };
                          }
                          return updated;
                        });
                      };
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                  >
                    {recipients.find(r => r.email === email)?.name || 'Sign Here'}
                  </div>
                ))}
            </div>
              <button
                type="button"
                className="btn btn-sm btn-secondary pdf-nav-side next"
                disabled={pageNumber >= numPages}
                onClick={() => setPageNumber(pageNumber + 1)}
              >
                Next
              </button>
              <div className="pdf-page-indicator">Page {pageNumber} / {numPages || 1}</div>
            </div>
          </div>

          <div className="pdf-side-controls" style={{ height: 'fit-content' }}>
            <button type="button" className={`btn btn-secondary ${applyAllPages ? 'btn-disabled' : ''}`} onClick={handleAddSignatureClick} disabled={applyAllPages} style={{ width: '100%' }}>
              Choose Pages{selectedPages.length > 0 ? ` (${selectedPages.length})` : ''}
            </button>

            <div className="control-inline-options">
              <div className="checkbox-control">
                <input type="checkbox" checked={applyAllPages} onChange={handleApplyAllPagesChange} />
                <label>Apply to All Pages</label>
              </div>
              <div className="checkbox-control">
                <input type="checkbox" checked={dragAllAtOnce} onChange={() => setDragAllAtOnce(!dragAllAtOnce)} />
                <label>Drag All Pages</label>
              </div>
            </div>

            <div className="button-group" style={{ marginTop: 30, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSaveRequest(e);
                }} 
                disabled={isSaving}
              >
                <FiSave style={{ marginRight: 5 }} /> {isSaving ? "Sending..." : "Send Request"}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setStep(1)} disabled={isSaving}>
                Back to Config
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal to select pages (EXISTING LOGIC PRESERVED) */}
      {selectingPages && (
        <div className="modal-overlay">
          <div className="modal-content page-picker-modal">
            <div className="page-picker-header">
              <div className="page-picker-top-row">
                <h3>Select Pages</h3>
                <span className="page-picker-count">{tempSelectedPages.length} selected</span>
              </div>
              <div className="page-picker-actions">
                <button type="button" className="page-chip-btn" onClick={handleSelectAllPages}>Select all</button>
                <button type="button" className="page-chip-btn" onClick={handleClearPageSelection}>Clear</button>
              </div>
            </div>
            <div className="page-picker-grid-wrap">
              <div className="page-picker-grid">
                {Array.from({ length: numPages }, (_, idx) => (
                  <label key={idx} className="page-picker-item">
                    <input
                      type="checkbox"
                      checked={tempSelectedPages.includes(idx + 1)}
                      onChange={() => togglePageSelection(idx + 1)}
                    />
                    <span>Page {idx + 1}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="page-picker-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCancelPageSelection}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleConfirmPages}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Modal Popup */}
      {showValidationModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }} onClick={() => setShowValidationModal(false)}>
          <div className="modal-content" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            position: 'relative'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px',
              gap: '12px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: '#FEF3C7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <FiAlertTriangle size={24} color="#F59E0B" />
              </div>
              <h3 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 600,
                color: '#1F2937'
              }}>Signature Boxes Required</h3>
            </div>
            <p style={{
              color: '#4B5563',
              fontSize: '15px',
              lineHeight: '1.6',
              marginBottom: '25px'
            }}>
              {validationModalMessage}
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowValidationModal(false)}
                style={{ padding: '10px 20px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InitiateRequest;
